import { useMemo, useState } from 'preact/hooks';
import { MOBILE_MODEL_ALL, STOCK_STATUS_ALL, matchStockStatus } from '../resource/stockStatus';

/**
 * กรองและสรุปผลรายงาน stock เคลมฝั่ง client ทั้งหมด (API ยิงครั้งเดียวต่อการเปิดหน้า/รีเฟรช)
 * itemAvailableQty, pendingQty, netAvailableQty ซ้ำกันทุกแถวของ itemId เดียวกัน ห้ามรวมแบบ sum ต่อแถว
 * reservedQty/reservedClaimCodes/effectiveAvailableQty ก็เป็นค่าระดับ itemId เช่นกัน จึงต้องนับครั้งเดียวต่อ itemId
 */
export const useStockClaimFilters = (items) => {
  const [keyword, setKeyword] = useState('');
  const [mobileModel, setMobileModel] = useState(MOBILE_MODEL_ALL);
  const [stockStatus, setStockStatus] = useState(STOCK_STATUS_ALL);
  const [reservedOnly, setReservedOnly] = useState(false);

  const mobileModelOptions = useMemo(() => {
    const seen = new Set();
    const options = [{ value: MOBILE_MODEL_ALL, label: 'ทั้งหมด' }];
    (items ?? []).forEach((item) => {
      const model = item?.mobileModel;
      if (model && !seen.has(model)) {
        seen.add(model);
        options.push({ value: model, label: model });
      }
    });
    return options;
  }, [items]);

  const filteredRows = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    return (items ?? []).filter((item) => {
      if (kw) {
        const matchItemId = `${item?.itemId ?? ''}`.toLowerCase().includes(kw);
        const matchItemName = `${item?.itemName ?? ''}`.toLowerCase().includes(kw);
        if (!matchItemId && !matchItemName) {
          return false;
        }
      }
      if (mobileModel !== MOBILE_MODEL_ALL && item?.mobileModel !== mobileModel) {
        return false;
      }
      if (!matchStockStatus(item?.effectiveAvailableQty, stockStatus)) {
        return false;
      }
      if (reservedOnly && !(item?.reservedQty > 0)) {
        return false;
      }
      return true;
    });
  }, [items, keyword, mobileModel, stockStatus, reservedOnly]);

  const summary = useMemo(() => {
    const shortItemIds = new Set();
    const reservedByItemId = new Map();

    filteredRows.forEach((row) => {
      const itemId = row?.itemId;
      if (itemId == null) {
        return;
      }
      if ((row?.effectiveAvailableQty ?? 0) <= 0) {
        shortItemIds.add(itemId);
      }
      if (!reservedByItemId.has(itemId)) {
        reservedByItemId.set(itemId, row?.reservedQty ?? 0);
      }
    });

    let totalReserved = 0;
    reservedByItemId.forEach((qty) => {
      totalReserved += qty;
    });

    return {
      rowCount: filteredRows.length,
      shortItemCount: shortItemIds.size,
      totalReserved,
    };
  }, [filteredRows]);

  const formatDataForExcel = (rows) =>
    (rows ?? []).map((row) => {
      const codes = (row?.reservedClaimCodes ?? []).join(', ');
      return {
        itemId: row?.itemId ?? '',
        itemName: row?.itemName ?? '',
        mobileModelDisplay: [row?.mobileBrand, row?.mobileModel].filter(Boolean).join(' / ') || '',
        company: row?.company ?? '',
        availableQty: row?.availableQty ?? 0,
        pendingQty: row?.pendingQty ?? 0,
        netAvailableQty: row?.netAvailableQty ?? 0,
        reservedQtyDisplay: codes
          ? `${row?.reservedQty ?? 0} (${codes})`
          : `${row?.reservedQty ?? 0}`,
        effectiveAvailableQty: row?.effectiveAvailableQty ?? 0,
        updatedAt: row?.updatedAt ?? '',
      };
    });

  return {
    keyword,
    setKeyword,
    mobileModel,
    setMobileModel,
    stockStatus,
    setStockStatus,
    reservedOnly,
    setReservedOnly,
    mobileModelOptions,
    filteredRows,
    summary,
    formatDataForExcel,
  };
};
